/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {PERMISSIONS} from '@agent-assist/common/lib/admin/rbac';
import {ElementType} from 'react';
import {FeatureFlags, type FeatureFlagSet} from './flags';
import AgentsCreatePage from './pages/agents/AgentsCreatePage';
import AgentsEditPage from './pages/agents/AgentsEditPage';
import AgentsListPage from './pages/agents/AgentsListPage';
import {AnalyticsPage} from './pages/AnalyticsPage';
import {CalibrationPage} from './pages/CalibrationPage';
import ContactImportsListPage from './pages/ContactImportsListPage';
import {ContactImportViewPage} from './pages/ContactImportViewPage';
import ContactViewPage from './pages/ContactViewPage';
import {DevPage} from './pages/DevPage';
import {PromptViewPage} from './pages/PromptViewPage';
import CreateQAPrompts from './pages/QA/prompts/createQAPrompts';
import QAPrompts from './pages/QA/prompts/ListQAPrompts';
import QAPublishedPrompt from './pages/QA/prompts/PublishedQAPrompt';
import Settings from './pages/settings/Settings';

type RequiredPermissions = string;
type ProtectedRoute = {
  path: string;
  component: ElementType;
  requiredPermissions?: RequiredPermissions[];
  featureFlag?: FeatureFlags;
};

/**
 * ROUTES
 */
export const ROUTES = {
  AGENTS_CREATE: '/agents/create',
  AGENTS_EDIT: (agentId: string) => `/agents/${agentId}/edit`,
  AGENTS_EDIT_PATH: '/agents/:agentId/edit',
  AGENTS_LIST: '/agents',
  ANALYTICS: '/analytics',
  ANALYTICS_CONTACT_IMPORT: '/analytics/:criteriaId',
  ANALYTICS_PROMPT_REVISION: '/analytics/:criteriaId',
  AUTH_CALLBACK: '/auth-callback',
  CALIBRATION: '/calibration',
  CONTACT_IMPORTS_LIST: '/contacts',
  CONTACT_IMPORT_VIEW: '/contacts/:contactImportId',
  CONTACT_VIEW: (contactImportId: string, contactId: string) => `/contacts/${contactImportId}/contact/${contactId}`,
  CONTACT_VIEW_PATH: '/contacts/:contactImportId/contact/:contactId',
  DATA_DIALOG: '/data-dialog',
  DEFAULT: '/analytics',
  DEV: '/dev',
  HOME: '/',
  LOGOUT: '/logout',
  NOT_FOUND: '*',
  PROMPTS: '/prompts',
  PROMPT_CREATE: '/prompts/create',
  PROMPT_EDIT: '/prompts/edit/:promptRevisionId',
  PROMPT_LIVE: '/prompts/live',
  PROMPT_VIEW: '/prompts/:promptRevisionId',
  SETTINGS: '/settings',
};

export const DEFAULT_LANDING_ROUTES_IN_ORDER = [ROUTES.ANALYTICS, ROUTES.AGENTS_LIST];

// ORDER ROUTES BY SPECIFICITY
export const protectedRoutes: ProtectedRoute[] = [
  // Contact & Contact Imports
  {
    path: ROUTES.CONTACT_IMPORTS_LIST,
    component: ContactImportsListPage,
    requiredPermissions: [PERMISSIONS.IMPORTS_READ],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  {
    path: ROUTES.CONTACT_IMPORT_VIEW,
    component: ContactImportViewPage,
    requiredPermissions: [PERMISSIONS.IMPORTS_READ],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  {
    path: ROUTES.CONTACT_VIEW_PATH,
    component: ContactViewPage,
    requiredPermissions: [PERMISSIONS.CONTACTS_READ],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  // Agents
  {
    path: ROUTES.AGENTS_CREATE,
    component: AgentsCreatePage,
    requiredPermissions: [PERMISSIONS.TRUSSTED_AGENT_CREATE],
  },
  {
    path: ROUTES.AGENTS_EDIT_PATH,
    component: AgentsEditPage,
    requiredPermissions: [PERMISSIONS.TRUSSTED_AGENT_UPDATE],
  },
  {
    path: ROUTES.AGENTS_LIST,
    component: AgentsListPage,
    requiredPermissions: [PERMISSIONS.TRUSSTED_AGENT_READ],
  },
  // Analytics
  {
    path: ROUTES.ANALYTICS_PROMPT_REVISION,
    component: AnalyticsPage,
    requiredPermissions: [PERMISSIONS.INSIGHTS_READ],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  {
    path: ROUTES.ANALYTICS_CONTACT_IMPORT,
    component: AnalyticsPage,
    requiredPermissions: [PERMISSIONS.INSIGHTS_READ],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  {
    path: ROUTES.ANALYTICS,
    component: AnalyticsPage,
    requiredPermissions: [PERMISSIONS.INSIGHTS_READ],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  // Calibration
  {
    path: ROUTES.CALIBRATION,
    component: CalibrationPage,
    requiredPermissions: [PERMISSIONS.RATING_PROMPT_CREATE],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  // Prompts
  {
    path: ROUTES.PROMPT_EDIT,
    component: CreateQAPrompts,
    requiredPermissions: [PERMISSIONS.PROMPT_CREATE, PERMISSIONS.PROMPT_UPDATE],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  {
    path: ROUTES.PROMPT_LIVE,
    component: QAPublishedPrompt,
    requiredPermissions: [PERMISSIONS.PROMPTS_READ],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  {
    path: ROUTES.PROMPT_CREATE,
    component: CreateQAPrompts,
    requiredPermissions: [PERMISSIONS.PROMPT_CREATE],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  {
    path: ROUTES.PROMPT_VIEW,
    component: PromptViewPage,
    requiredPermissions: [PERMISSIONS.PROMPTS_READ],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  {
    path: ROUTES.PROMPTS,
    component: QAPrompts,
    requiredPermissions: [PERMISSIONS.PROMPTS_READ],
    featureFlag: FeatureFlags.functionInsightsAnalytics,
  },
  // Settings
  {
    path: ROUTES.SETTINGS,
    component: Settings,
    requiredPermissions: [PERMISSIONS.SETTINGS_READ],
  },
  // Development
  {
    path: ROUTES.DEV,
    component: DevPage,
  },
];

export const getValidLandingRoute = (flags: FeatureFlagSet): string | undefined => {
  return DEFAULT_LANDING_ROUTES_IN_ORDER.find((route) =>
    protectedRoutes.some(({path, featureFlag}) => {
      const isPathMatching = path === route;
      const isFeatureFlagValid = !featureFlag || flags[featureFlag];
      return isPathMatching && isFeatureFlagValid;
    }),
  );
};
